import { useContext } from 'react'
import {
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
} from '@ea-lab/reactive-json'

/**
 * ReactiveJSON primitive — renders the display label of a value that may be
 * EITHER a Drupal taxonomy term-ref object (`{tid, vocabulary, name}`) OR a
 * plain string.
 *
 * Why it exists : a decoupled list can mix object instances whose same field
 * is a `term-ref` on one bundle (stored as the object above) and a `text`
 * field on another (stored as a string) — e.g. the Académie search lists
 * `academie_article` (category = term-ref) alongside `academie_immortel` /
 * `academie_evenement` (category = text). Binding `~.…category` directly then
 * crashes with React error #31 ("objects are not valid as a React child") on
 * the term-ref rows, while `~.…category.name` silently drops the string rows.
 * This primitive resolves both shapes to a single label.
 *
 * Resolution : object → `name` (then `label`, then `value`) ; scalar → itself.
 * Renders nothing for empty / null. It outputs bare text, so wrap it in the
 * styled element you want (it does not render its own tag).
 *
 * YAML usage (inside a content array):
 *   - type: span
 *     attributes: { class: 'uppercase …' }
 *     content:
 *       - { type: TermLabel, value: ~.objectData.data.category }
 */
export const TermLabel = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const value = evaluateTemplateValue({
    valueToEvaluate: props.value,
    globalDataContext,
    templateContext,
  })

  const label =
    value && typeof value === 'object'
      ? value.name ?? value.label ?? value.value ?? ''
      : value

  if (label == null || label === '') return null
  return <>{label}</>
}
